import { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import {
  useCreateSolarEstimationMutation,
  useInitiateSolarEstimationMutation,
  useLazyViewSolarEstimationQuery,
  useUpdateEstimationMutation,
} from "../../../redux/api/solar-estimation/solarEstimationAPI";
import { selectAuth } from "../../../redux/features/auth/auth-slice";
import { selectCountry } from "../../../redux/features/user/user-slice";
import {
  IViewSolarEstimationData,
  IViewSolarEstimationOutputParams,
  IViewSolarUserSelectedProducts,
  ViewSolarEstimationResponseDTO,
} from "../../../shared/oversight-core/dtos/response-dtos/view-solar-estimation-response-dto";
import { EApplicationType } from "../../../shared/oversight-core/enums/application-type";
import { EEnergyNeedEditParameter } from "../../../shared/oversight-core/enums/energy-need-edit-parameters";
import { EInverterPhaseType } from "../../../shared/oversight-core/enums/inverter-phase-type";
import { ESolarSolutionType } from "../../../shared/oversight-core/enums/sola-solution-type";
import { ESolarSimBrands } from "../../../shared/oversight-core/enums/solar-sim-brands";
import { ESolarSimLocalStorage } from "../../../shared/oversight-core/enums/solar-sim-local-storage";
import { ESolarSystemType } from "../../../shared/oversight-core/enums/solar-system-type ";
import { EUtilityOptionType } from "../../../shared/oversight-core/enums/utility-option-type";
import { AppRoute } from "../../../shared/oversight-core/interfaces/app-routes";
import { Configurations } from "../../../shared/oversight-core/interfaces/configurations";
import { IEstimatorDetails } from "../../../shared/oversight-core/interfaces/estimator-details";
import { IWaterHeaterSave } from "../../../shared/oversight-core/interfaces/water-heater-save";
import { Option } from "../../../shared/oversight-core/ui-elements/app-select/app-select";
import MaterialIcon from "../../../shared/oversight-core/ui-elements/material-icon/material-icon";
import SpinnerModal from "../../../shared/oversight-core/ui-elements/spinner/spinner";
import { ISteps } from "../../../shared/oversight-core/ui-elements/steps-number/steps-number";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../shared/oversight-core/utils/toast";
import ApprovedSystem from "../components/approved-system/approved-system";
import AssembleSystem from "../components/assemble-system/assemble-system";
import EditParameterModal from "../components/edit-parameter-modal/edit-parameter-modal";
import IncludeSystemCard from "../components/include-system-card/include-system-card";
import SaveDataModal from "../components/save-data-modal/save-data-modal";
import SelectedWaterHeaterSystemCard from "../components/selected-water-heater-system-card/selected-water-heater-system-card";
import StepsHeader from "../components/steps-header/steps-header";
import styles from "./build-water-heater-system.module.scss";

const steps: ISteps[] = [
  { id: 1, stepName: "Assemble System", stepIcon: "widgets" },
];

interface ILocationState {
  estimationId: string;
  estimationToken: string;
  numberOfUsers: number;
  applicationType: EApplicationType;
  utilityOption: EUtilityOptionType;
  solarSolutionType: ESolarSolutionType;
}

const applicationTypeOptions: Option[] = [
  {
    value: EApplicationType.RESIDENTIAL,
    label: "Residential",
  },
  {
    value: EApplicationType.HOTELS,
    label: "Hotels",
  },
  {
    value: EApplicationType.HOSTELS,
    label: "Hostels",
  },
  {
    value: EApplicationType.SCHOOLS,
    label: "Schools",
  },
];

const utilityOptions: Option[] = [
  {
    value: EUtilityOptionType.SHOWER,
    label: "Shower",
  },
  {
    value: EUtilityOptionType.BATHTUB_JACUZZI,
    label: "Bathtub/Jacuzzi",
  },
];

const BuildWaterHeaterSystem = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const locationState = location.state as ILocationState;
  const auth = useSelector(selectAuth);
  const country = useSelector(selectCountry);

  const [selectedStep, setSelectedStep] = useState(1);
  const [editParameterType, setEditParameterType] =
    useState<EEnergyNeedEditParameter>(EEnergyNeedEditParameter.TITLE);
  const [editParameterValue, setEditParameterValue] = useState<number | string>(
    0
  );
  const [title, setTitle] = useState("");
  const [waterDemand, setWaterDemand] = useState(0);
  const [numberOfUsers, setNumberOfUsers] = useState(0);
  const [showEditParameterModal, setShowEditParameterModal] = useState(false);
  const [isReset, setIsReset] = useState(false);
  const [applicationType, setApplicationType] = useState<Option>({
    ...applicationTypeOptions[0],
  });
  const [utilityOption, setUtilityOption] = useState<Option>({
    ...utilityOptions[0],
  });
  const [solarSolutionType, setSolarSolutionType] =
    useState<ESolarSolutionType>(ESolarSolutionType.BOTH);
  const [includeOtherFees, setIncludeOtherFees] = useState(false);
  const [estimationId, setEstimationId] = useState(0);
  const [configurations, setConfigurations] = useState<Configurations>();
  const [estimatorDetails, setEstimatorDetails] = useState<IEstimatorDetails>({
    estimationId: "",
    estimationToken: "",
  });
  const [hasChanged, setHasChanged] = useState(false);
  const [assembleSystemData, setAssembleSystemData] = useState<
    IViewSolarEstimationData | undefined
  >(undefined);
  const [isResetRecommendations, setIsResetRecommendations] = useState(false);
  const [comment, setComment] = useState("");
  const [selectedSystemOutput, setSelectedSystemOutput] =
    useState<IViewSolarEstimationOutputParams>({
      totalWattHours: 0,
      totalCost: 0,
      systemVoltage: 0,
      totalAmpHours: 0,
      nightLoadAmpHours: 0,
      batteryAmpHours: 0,
      maxPowerInWatt: 0,
      grandTotal: 0,
      totalAccessoriesCost: 0,
      laborCost: 0,
      totalLiters: 0,
    });
  const [userSelectedProducts, setUserSelectedProducts] = useState<
    IViewSolarUserSelectedProducts[]
  >([]);
  const [currency, setCurrency] = useState("");
  const [isShowSaveDataModal, setIsShowSaveDataModal] = useState(false);
  const [isUpdateCurrentState, setIsUpdateCurrentState] = useState(false);
  const [controllerRequired, setControllerRequired] = useState(false);

  const hasHydrated = useRef(false);

  const [
    triggerInitiateSolarEstimation,
    { isLoading: isLoadingInitiateSolarEstimation },
  ] = useInitiateSolarEstimationMutation();
  const [
    triggerCreateSolarEstimation,
    { isLoading: isLoadingCreateSolarEstimation },
  ] = useCreateSolarEstimationMutation();
  const [
    triggerLazyViewSolarEstimation,
    { isLoading: isLoadingViewEstimation },
  ] = useLazyViewSolarEstimationQuery();
  const [triggerUpdateEstimation, { isLoading: isLoadingUpdateEstimation }] =
    useUpdateEstimationMutation();

  useEffect(() => {
    const globalConfigurations = localStorage.getItem("globalConfigurations");

    let mounted = true;

    const applyResponse = (res: ViewSolarEstimationResponseDTO) => {
      if (!mounted) return;

      const view = res?.estimationView;
      const input = view?.data?.inputParams ?? {};

      const selectedUtilityOption = utilityOptions.find(
        (u) => u.value === view?.utilityOptionType
      );

      const selectedApplicationType = applicationTypeOptions.find(
        (a) => a.value === view?.applicationType
      );

      setNumberOfUsers(input.numberOfUsers || 0);
      setSolarSolutionType(view?.estimationType);
      setTitle((ps) => view?.title ?? ps);
      setIncludeOtherFees(Boolean(view?.installationCostRequested));
      setControllerRequired(view?.controllerRequired);
      setEstimationId(view?.estimationId);
      setWaterDemand(input.waterDemand || 0);

      if (selectedApplicationType) {
        setApplicationType({
          value: selectedApplicationType.value,
          label: selectedApplicationType.label,
        });
      }

      if (selectedUtilityOption) {
        setUtilityOption({
          value: selectedUtilityOption.value,
          label: selectedUtilityOption.label,
        });
      }

      if (view?.data?.preferences) {
        if (globalConfigurations) {
          const parsedGlobalConfigurations: Configurations =
            JSON.parse(globalConfigurations);

          setConfigurations(parsedGlobalConfigurations);

          return;
        }

        setConfigurations(view.data.preferences);
      }
    };

    const shouldUseEstimationId = Boolean(
      auth.token && estimatorDetails.estimationId
    );
    const shouldUseEstimatorToken = Boolean(
      !auth.token && estimatorDetails.estimationToken
    );

    if (!shouldUseEstimationId && !shouldUseEstimatorToken) return;

    (async () => {
      try {
        const params = shouldUseEstimationId
          ? { estimationId: estimatorDetails.estimationId }
          : { estimatorToken: estimatorDetails.estimationToken };

        const res = await triggerLazyViewSolarEstimation(params).unwrap();
        applyResponse(res);
      } catch (err) {
        showErrorMessage("Failed to load estimation details");
      }
    })();

    return () => {
      mounted = false;
    };
  }, [
    auth.token,
    estimatorDetails.estimationId,
    estimatorDetails.estimationToken,
    isUpdateCurrentState,
  ]);

  useEffect(() => {
    if (!estimatorDetails.estimationId || !estimatorDetails.estimationToken) {
      return;
    }

    if (hasHydrated.current) {
      return;
    }

    hasHydrated.current = true;

    const waterHeaterSave = localStorage.getItem("waterHeaterSave");

    if (waterHeaterSave) {
      const parsedWaterHeaterSave: IWaterHeaterSave =
        JSON.parse(waterHeaterSave);

      const selectedUtilityOption = utilityOptions.find(
        (u) => u.value === parsedWaterHeaterSave.utilityOptionType
      );

      const selectedApplicationType = applicationTypeOptions.find(
        (a) => a.value === parsedWaterHeaterSave.applicationType
      );

      if (!parsedWaterHeaterSave.isCalledSave) {
        onSave(
          parsedWaterHeaterSave.title,
          parsedWaterHeaterSave.estimationType,
          parsedWaterHeaterSave.applicationType,
          parsedWaterHeaterSave.utilityOptionType,
          parsedWaterHeaterSave.numberOfUsers
        );
      }

      setTitle(parsedWaterHeaterSave.title);
      setIncludeOtherFees(parsedWaterHeaterSave.includeOtherFees);
      setNumberOfUsers(parsedWaterHeaterSave.numberOfUsers);
      setSolarSolutionType(parsedWaterHeaterSave.estimationType);

      if (selectedApplicationType) {
        setApplicationType({
          value: selectedApplicationType.value,
          label: selectedApplicationType.label,
        });
      }

      if (selectedUtilityOption) {
        setUtilityOption({
          value: selectedUtilityOption.value,
          label: selectedUtilityOption.label,
        });
      }
    }
  }, [estimatorDetails.estimationId, estimatorDetails.estimationToken]);

  useEffect(() => {
    if (pathname.includes("assemble-system")) {
      setSelectedStep(1);
      return;
    }

    if (pathname.includes("system-summary")) {
      setSelectedStep(2);
    }
  }, [pathname]);

  useEffect(() => {
    if (!pathname.includes("assemble-system")) return;

    const { estimationId, estimationToken } = estimatorDetails;

    if (!estimationId || !estimationToken) {
      const unparsedEstimatorDetails = localStorage.getItem(
        ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER
      );

      if (unparsedEstimatorDetails) {
        const parsedEstimatorDetails: IEstimatorDetails = JSON.parse(
          unparsedEstimatorDetails
        );
        setEstimatorDetails(parsedEstimatorDetails);
        return;
      }

      return;
    }

    if (!country) {
      return;
    }

    const fetchPreferences = async () => {
      try {
        let brandPriority: ESolarSimBrands[] = [];
        if (configurations) {
          if (Array.isArray(configurations.BRAND_PRIORITY_LIST)) {
            brandPriority = configurations.BRAND_PRIORITY_LIST.map(
              (bp) => bp.brand
            );
          }
        }

        const brandPriorityPreference = configurations
          ? Boolean(configurations.BRAND_PRIORITY)
          : false;
        const optimizeForCost = configurations
          ? Boolean(configurations.OPTIMIZE_FOR_COST)
          : false;
        const preferHighEfficiencyPanels = configurations
          ? Boolean(configurations.PREFER_HIGH_EFFICIENCY_PANELS)
          : false;
        const recommendExpandableSystems = configurations
          ? Boolean(configurations.RECOMMEND_EXPANDABLE_SYSTEMS)
          : false;

        if (brandPriority.length === 0) {
          return;
        }

        await triggerCreateSolarEstimation({
          estimationId: auth.token ? estimationId : "",
          estimatorToken: auth.token ? "" : estimationToken,
          title: title ? title : null,
          estimationType: solarSolutionType,
          applicationType: applicationType.value as EApplicationType,
          utilityOptionType: utilityOption.value as EUtilityOptionType,
          solarEstimationType: null,
          systemType: null,
          inverterPhaseType: null,
          monthlyRequiredEnergy: null,
          maxRequiredPower: null,
          numberOfUsers: numberOfUsers,
          nightLoad: null,
          sunlightHours: null,
          estimationSpaceClusterId: null,
          estimationBasicScheduleId: null,
          isDraft: false,
          brandPriority,
          brandPriorityPreference,
          optimizeForCost,
          preferHighEfficiencyPanels,
          recommendExpandableSystems,
          countryCode: country,
          hasChanged: hasChanged || null,
          controllerRequired: controllerRequired,
        })
          .unwrap()
          .then(() => {
            localStorage.removeItem("globalConfigurations");
          });

        await triggerLazyViewSolarEstimation(
          auth.token
            ? {
                estimationId: estimationId,
              }
            : {
                estimatorToken: estimationToken,
              }
        )
          .unwrap()
          .then((viewRes) => {
            setEstimationId(viewRes.estimationView.estimationId);
            setAssembleSystemData({ ...viewRes.estimationView.data });
            setSelectedSystemOutput(
              viewRes.estimationView.data.outputParams || {
                totalWattHours: 0,
                totalCost: 0,
                systemVoltage: 0,
                totalAmpHours: 0,
                nightLoadAmpHours: 0,
                batteryAmpHours: 0,
                maxPowerInWatt: 0,
                grandTotal: 0,
                totalAccessoriesCost: 0,
                laborCost: 0,
                totalLiters: 0,
              }
            );
            setWaterDemand(
              viewRes.estimationView.data.inputParams.waterDemand || 0
            );
          });
      } catch {
        setEstimationId(0);
        setAssembleSystemData(undefined);
        setWaterDemand(0);
      }
    };

    fetchPreferences();
  }, [
    pathname,
    estimatorDetails.estimationId,
    estimatorDetails.estimationToken,
    title,
    isResetRecommendations,
    country,
    configurations,
    solarSolutionType,
    applicationType,
    utilityOption,
    numberOfUsers,
    controllerRequired,
  ]);

  useEffect(() => {
    if (!estimatorDetails.estimationToken || !estimatorDetails.estimationId) {
      const unparsedEstimatorDetails = localStorage.getItem(
        ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER
      );

      if (unparsedEstimatorDetails) {
        const parsedEstimatorDetails: IEstimatorDetails = JSON.parse(
          unparsedEstimatorDetails
        );
        setEstimatorDetails(parsedEstimatorDetails);
        return;
      }
    }

    if (
      pathname.includes("system-summary") &&
      estimatorDetails.estimationToken
    ) {
      triggerLazyViewSolarEstimation(
        auth.token
          ? {
              estimationId: estimatorDetails.estimationId,
            }
          : {
              estimatorToken: estimatorDetails.estimationToken,
            }
      )
        .unwrap()
        .then((res) => {
          setComment(res.estimationView.adminComment || "");
          setSelectedSystemOutput(
            res.estimationView.data.outputParams || {
              totalWattHours: 0,
              totalCost: 0,
              systemVoltage: 0,
              totalAmpHours: 0,
              nightLoadAmpHours: 0,
              batteryAmpHours: 0,
              maxPowerInWatt: 0,
              grandTotal: 0,
              totalAccessoriesCost: 0,
              laborCost: 0,
              totalLiters: 0,
            }
          );
          setUserSelectedProducts(
            res.estimationView.data.userSelectedProducts || []
          );
          setAssembleSystemData({ ...res.estimationView.data });
          setCurrency(res.estimationView.currency);
        })
        .catch(() => {
          setComment("");
          setSelectedSystemOutput({
            totalWattHours: 0,
            totalCost: 0,
            systemVoltage: 0,
            totalAmpHours: 0,
            nightLoadAmpHours: 0,
            batteryAmpHours: 0,
            maxPowerInWatt: 0,
            grandTotal: 0,
            totalAccessoriesCost: 0,
            laborCost: 0,
            totalLiters: 0,
          });
          setUserSelectedProducts([]);
          setAssembleSystemData(undefined);
          setCurrency("");
        });
    }
  }, [
    pathname,
    estimatorDetails.estimationToken,
    estimatorDetails.estimationId,
  ]);

  useEffect(() => {
    if (!pathname.includes("system-summary")) {
      setUserSelectedProducts([]);
      setAssembleSystemData(undefined);
      setCurrency("");
    }

    if (!pathname.includes("assemble-system")) {
      setAssembleSystemData(undefined);
    }
  }, [pathname]);

  useEffect(() => {
    if (!locationState) return;

    if (locationState.estimationId || locationState.estimationToken) {
      setEstimatorDetails({
        estimationId: locationState.estimationId,
        estimationToken: locationState.estimationToken,
      });
      return;
    }

    triggerInitiateSolarEstimation({
      estimationType: locationState.solarSolutionType,
      applicationType: locationState.applicationType,
      utilityOptionType: locationState.utilityOption,
      solarEstimationType: null,
      systemType: null,
      inverterPhaseType: null,
      monthlyRequiredEnergy: null,
      maxRequiredPower: null,
      numberOfUsers: locationState.numberOfUsers,
      nightLoad: null,
      sunlightHours: null,
      countryCode: country,
    })
      .unwrap()
      .then((res) => {
        const selectedUtilityOption = utilityOptions.find(
          (u) => u.value === locationState.utilityOption
        );

        const selectedApplicationType = applicationTypeOptions.find(
          (a) => a.value === locationState.applicationType
        );

        setEstimatorDetails({
          estimationId: res.id,
          estimationToken: res.token,
        });
        setNumberOfUsers(locationState.numberOfUsers);
        setSolarSolutionType(locationState.solarSolutionType);

        if (selectedApplicationType) {
          setApplicationType({
            value: selectedApplicationType.value,
            label: selectedApplicationType.label,
          });
        }

        if (selectedUtilityOption) {
          setUtilityOption({
            value: selectedUtilityOption.value,
            label: selectedUtilityOption.label,
          });
        }

        localStorage.setItem(
          ESolarSimLocalStorage.ESTIMATOR_DETAILS_WATER_HEATER,
          JSON.stringify({
            estimationId: res.id,
            estimationToken: res.token,
          })
        );

        triggerLazyViewSolarEstimation(
          auth.token
            ? {
                estimationId: res.id,
              }
            : {
                estimatorToken: res.token,
              }
        )
          .unwrap()
          .then((viewRes) => {
            setEstimationId(viewRes.estimationView.estimationId);
          })
          .catch(() => {
            setEstimationId(0);
          });
      });
  }, [
    triggerInitiateSolarEstimation,
    triggerLazyViewSolarEstimation,
    locationState,
  ]);

  const onStepClick = (stepNumber: number) => {
    if (selectedStep === stepNumber) return;

    if (auth.token) {
      if (stepNumber === 1) {
        navigate(AppRoute.DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM);
        return;
      }

      if (stepNumber === 2) {
        navigate(AppRoute.DESIGN_SYSTEM_WATER_HEATER_SYSTEM_SUMMARY);
        return;
      }
      return;
    }

    if (stepNumber === 1) {
      navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM);
      return;
    }

    if (stepNumber === 2) {
      navigate(AppRoute.PUBLIC_DESIGN_SYSTEM_WATER_HEATER_SYSTEM_SUMMARY);
    }
  };

  const onSave = async (
    title: string,
    estimationType: ESolarSolutionType,
    applicationType: EApplicationType,
    utilityOptionType: EUtilityOptionType,
    numberOfUsers: number,
    stepNumber?: number
  ) => {
    if (selectedStep === stepNumber) return;

    if (!title.trim()) {
      showErrorMessage("Please provide a title before continuing");
      return;
    }

    const waterHeaterSave: IWaterHeaterSave = {
      title,
      estimationType,
      applicationType,
      utilityOptionType,
      numberOfUsers,
      isCalledSave: false,
      includeOtherFees: includeOtherFees,
    };

    if (!auth.token) {
      if (pathname.includes("assemble-system")) {
        localStorage.setItem(
          "redirect",
          AppRoute.DESIGN_SYSTEM_WATER_HEATER_ASSEMBLE_SYSTEM
        );
      } else if (pathname.includes("system-summary")) {
        localStorage.setItem(
          "redirect",
          AppRoute.DESIGN_SYSTEM_WATER_HEATER_SYSTEM_SUMMARY
        );
      }

      localStorage.setItem("waterHeaterSave", JSON.stringify(waterHeaterSave));
      setIsShowSaveDataModal(true);
      return;
    }

    try {
      waterHeaterSave.isCalledSave = true;
      localStorage.setItem("waterHeaterSave", JSON.stringify(waterHeaterSave));
      if (!estimatorDetails.estimationId || !estimatorDetails.estimationToken) {
        return;
      }

      await triggerUpdateEstimation({
        estimationId: auth.token ? estimatorDetails.estimationId : "",
        estimatorToken: auth.token ? "" : estimatorDetails.estimationToken,
        title,
        estimationType,
        applicationType: applicationType,
        utilityOptionType: utilityOptionType,
        solarEstimationType: null,
        systemType: null,
        inverterPhaseType: null,
        monthlyRequiredEnergy: null,
        maxRequiredPower: null,
        numberOfUsers: numberOfUsers,
        nightLoad: null,
        sunlightHours: null,
        estimationSpaceClusterId: null,
        estimationBasicScheduleId: null,
        isDraft: false,
        countryCode: country,
        installationCostRequested: includeOtherFees,
      })
        .unwrap()
        .then(() => {
          showSuccessMessage("Estimation Saved Successfully");
          if (stepNumber) {
            onStepClick(stepNumber);
          }
          setIsUpdateCurrentState((ps) => !ps);
        });
    } catch {
      showErrorMessage("Failed to save");
    }
  };

  const onEditParameter = (type: EEnergyNeedEditParameter, value: string) => {
    let newValue;
    if (type === EEnergyNeedEditParameter.TITLE) {
      newValue = value;
    } else {
      newValue = value.trim() === "" ? 0 : value;
    }

    switch (type) {
      case EEnergyNeedEditParameter.TITLE:
        setTitle(newValue as string);
        break;
      case EEnergyNeedEditParameter.NO_OF_USERS: {
        const previousValue = numberOfUsers;
        const nowValue = Number.isNaN(Number(newValue)) ? 0 : Number(newValue);

        if (previousValue !== nowValue) {
          setNumberOfUsers(
            Number.isNaN(Number(newValue)) ? 0 : Number(newValue)
          );
          setIsReset(true);
        }

        break;
      }
      default:
        break;
    }

    setShowEditParameterModal(false);
  };

  const onParameterEdit = (type: EEnergyNeedEditParameter) => {
    setEditParameterType(type);
    let editValue: string | number;
    if (type === EEnergyNeedEditParameter.TITLE) {
      editValue = title;
    } else {
      editValue = numberOfUsers;
    }
    setEditParameterValue(editValue);
    setShowEditParameterModal(true);
  };

  return (
    <>
      <StepsHeader
        steps={steps}
        selectedStep={selectedStep}
        onStepClick={(stepNumber: number) => {
          if (selectedStep === stepNumber) return;
          if (!title.trim()) {
            showErrorMessage("Please provide a title before continuing");
            return;
          }

          if (!auth.token) {
            onStepClick(stepNumber);
            return;
          }

          onSave(
            title,
            solarSolutionType,
            applicationType.value as EApplicationType,
            utilityOption.value as EUtilityOptionType,
            numberOfUsers,
            stepNumber
          );
        }}
        onSave={() => {
          onSave(
            title,
            solarSolutionType,
            applicationType.value as EApplicationType,
            utilityOption.value as EUtilityOptionType,
            numberOfUsers
          );
        }}
        isApproved={false}
      />
      <div className={`py-3 ${styles.mainContent}`}>
        <div
          className={`estimation-container rounded-3 ${
            auth.token ? " mx-0" : "mx-4"
          }`}
        >
          <Row className="align-items-center justify-content-between py-2 px-4">
            <Col className="col-auto col-xl-2">
              <Row>
                <Col className="text-color-3 font-size-14">#{estimationId}</Col>
              </Row>
              <Row className="align-items-center">
                <Col className="col-auto title-2 font-weight-600 font-size-18 pe-0">
                  {title === "" ? "Title Goes Here" : title}
                </Col>
                <Col className="col-auto ps-1">
                  <MaterialIcon
                    icon="border_color"
                    color="var(--color-edit-icon-2)"
                    size={12}
                    className="cursor-pointer"
                    onClick={() => {
                      onParameterEdit(EEnergyNeedEditParameter.TITLE);
                    }}
                  />
                </Col>
              </Row>
            </Col>
            <Col className="col-12 col-xl-auto mt-3 mt-xl-0">
              <Row className="align-items-stretch">
                <Col className="col-auto">
                  <SelectedWaterHeaterSystemCard
                    onParameterEdit={onParameterEdit}
                    waterDemand={waterDemand}
                    totalLiters={selectedSystemOutput.totalLiters || 0}
                    numberOfUsers={numberOfUsers}
                    applicationType={applicationType}
                    utilityOption={utilityOption}
                    onApplicationTypeChange={(option: Option) => {
                      setApplicationType(option);
                      setIsReset(true);
                    }}
                    onUtilityOptionChange={(option: Option) => {
                      setUtilityOption(option);
                      setIsReset(true);
                    }}
                    isHideDeleteIcon
                    isHideEditIcon={selectedStep === 2}
                  />
                </Col>
                {selectedStep === 2 ? (
                  <></>
                ) : (
                  <Col>
                    <IncludeSystemCard
                      icon="solar_power"
                      text="Include Solar Energy System as Well?"
                      onClick={() => {
                        if (auth.token) {
                          navigate(AppRoute.DESIGN_SYSTEM, {
                            state: {
                              step: 2,
                              numberOfUsers: numberOfUsers,
                              utilityOption:
                                utilityOption.value as EUtilityOptionType,
                              applicationType:
                                applicationType.value ===
                                EApplicationType.RESIDENTIAL
                                  ? EApplicationType.RESIDENTIAL
                                  : EApplicationType.INSTITUTIONS,
                              institution:
                                applicationType.value as EApplicationType,
                              estimationId: estimatorDetails.estimationId,
                              estimationToken: estimatorDetails.estimationToken,
                            },
                          });
                          return;
                        }

                        navigate(AppRoute.PUBLIC_DESIGN_SYSTEM, {
                          state: {
                            step: 2,
                            numberOfUsers: numberOfUsers,
                            utilityOption:
                              utilityOption.value as EUtilityOptionType,
                            applicationType:
                              applicationType.value ===
                              EApplicationType.RESIDENTIAL
                                ? EApplicationType.RESIDENTIAL
                                : EApplicationType.INSTITUTIONS,
                            institution:
                              applicationType.value as EApplicationType,
                            estimationId: estimatorDetails.estimationId,
                            estimationToken: estimatorDetails.estimationToken,
                          },
                        });
                      }}
                      textWidth="150px"
                    />
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
          {selectedStep === 1 ? (
            <AssembleSystem
              assembleSystemData={assembleSystemData}
              estimatorDetails={estimatorDetails}
              selectedSystemOutput={selectedSystemOutput}
              setSelectedSystemOutput={setSelectedSystemOutput}
              setIsResetRecommendations={setIsResetRecommendations}
              configurations={configurations}
              sendConfigurations={(configurations: Configurations) => {
                setConfigurations({ ...configurations });
              }}
              isReset={isReset}
              setIsReset={setIsReset}
              setHasChanged={setHasChanged}
              estimationNo={estimationId}
              setDischargeRate={() => {
                0;
              }}
              estimationType={solarSolutionType}
              setWaterDemand={setWaterDemand}
              controllerRequired={controllerRequired}
              setControllerRequired={setControllerRequired}
              isDisabledControllerSwitch={
                isLoadingCreateSolarEstimation ||
                isLoadingViewEstimation ||
                isLoadingUpdateEstimation
              }
            />
          ) : (
            <ApprovedSystem
              userSelectedProducts={userSelectedProducts}
              assembleSystemData={assembleSystemData}
              onSave={() => {
                onSave(
                  title,
                  solarSolutionType,
                  applicationType.value as EApplicationType,
                  utilityOption.value as EUtilityOptionType,
                  numberOfUsers
                );
              }}
              currency={currency}
              estimatorDetails={estimatorDetails}
              includeOtherFees={includeOtherFees}
              setIncludeOtherFees={setIncludeOtherFees}
              comment={comment}
              title={title}
              country={country}
              monthlyRequiredEnergy={0}
              maxRequiredPower={0}
              systemType={ESolarSystemType.BACKUP}
              inverterPhaseType={EInverterPhaseType.NONE}
              nightLoad={0}
              sunlightHours={0}
              estimationSpaceClusterId={null}
              estimationNo={estimationId}
              estimationType={solarSolutionType}
              applicationType={applicationType.value as EApplicationType}
              utilityOptionType={utilityOption.value as EUtilityOptionType}
              numberOfUsers={numberOfUsers}
            />
          )}
        </div>
      </div>
      <EditParameterModal
        show={showEditParameterModal}
        onCancel={() => {
          setShowEditParameterModal(false);
        }}
        onClose={() => {
          setShowEditParameterModal(false);
        }}
        type={editParameterType}
        currentValue={editParameterValue}
        onEditParameter={onEditParameter}
      />
      <SpinnerModal
        show={
          isLoadingInitiateSolarEstimation ||
          isLoadingCreateSolarEstimation ||
          isLoadingViewEstimation ||
          isLoadingUpdateEstimation
        }
      />
      <SaveDataModal
        show={isShowSaveDataModal}
        onClose={() => {
          const waterHeaterSave = localStorage.getItem("waterHeaterSave");

          if (waterHeaterSave) {
            const parsedWaterHeaterSave: IWaterHeaterSave =
              JSON.parse(waterHeaterSave);
            parsedWaterHeaterSave.isCalledSave = true;
            localStorage.setItem(
              "waterHeaterSave",
              JSON.stringify(parsedWaterHeaterSave)
            );
          }

          localStorage.removeItem("redirect");

          setIsShowSaveDataModal(false);
        }}
        onConfirm={() => {
          navigate(AppRoute.LOGIN);
          setIsShowSaveDataModal(false);
        }}
      />
    </>
  );
};

export default BuildWaterHeaterSystem;
